import { Show, Suspense, Match, Switch, For } from 'solid-js';
import {
	Button,
	Link,
	Picture,
	Container,
	Heading,
	Carousel,
	CarouselItem,
	Grid,
	GridThird,
	Section,
	GridTwoThirds,
	Hero,
} from '@troon/ui';
import { Meta, Title } from '@solidjs/meta';
import { useAction } from '@solidjs/router';
import { IconStarFilled } from '@troon/icons/star-filled';
import { IconStar } from '@troon/icons/star';
import { clientAction } from '@troon/api-client';
import { useFacility } from '../../../../providers/facility';
import { FacilityBrand } from '../../../../graphql';
import { getFacilitySchema } from '../../../../modules/schema/facility';
import { courseTypeString } from '../../../../components/facility/type-strings';
import { FacilityLocation } from '../../../../components/facility/location';
import { SupportBadge } from '../../../../components/support-badge';
import type { RouteDefinition } from '@solidjs/router';

export default function Course() {
	const facility = useFacility();
	const handleFavoriteAction = useAction(handleFavorite);

	return (
		<>
			<Title>{`${facility().name} | Golf with Access | Book tee times`}</Title>
			<Meta
				name="description"
				content={`${facility().description?.slice(0, 154) ?? 'Book tee times at your favorite courses with Access'}…`}
			/>
			<script type="application/ld+json" innerText={getFacilitySchema(facility())} />

			<Suspense fallback={<div>loading…</div>}>
				<Hero src={facility().hero} crop="entropy" />
				<Container>
					<Show when={facility().logo}>
						{(logo) => (
							<div class="relative -top-32 z-20 -mb-32 aspect-square size-36 rounded-lg border border-neutral bg-white p-4 shadow">
								<Picture
									preload
									src={logo()}
									alt=""
									mode="contain"
									width={200}
									height={200}
									sizes="9rem"
									class="w-full"
								/>
							</div>
						)}
					</Show>
					<header class="mb-6 flex flex-col items-stretch gap-6 border-b border-neutral-500 pb-6 md:mb-8 md:flex-nowrap md:pb-8">
						<div class="flex flex-wrap items-center justify-start gap-4 lg:gap-8">
							<div class="flex shrink grow flex-col gap-2 md:gap-4">
								<Heading as="h1">{facility().name}</Heading>
								<p class="order-first text-sm tracking-widest text-neutral-800 uppercase">
									<span class="sr-only">Course type:</span> {courseTypeString[facility().type]}
									<Show when={facility().brand === FacilityBrand.TroonPartner}> • Troon Partner Course</Show>
								</p>
								<Show when={facility().address}>
									{(address) => (
										<address class="text-sm text-neutral-800 not-italic" aria-label={`Address for ${facility().name}`}>
											{[address().street, address().city, [address().state, address().postalCode].join(' ')]
												.filter(Boolean)
												.join(', ')}
										</address>
									)}
								</Show>
								<SupportBadge
									points={facility().supportsAccessPoints}
									rates={facility().supportsTroonAccess}
									class="text-sm font-semibold"
								/>
							</div>

							<div class="flex grow basis-full flex-row justify-end gap-4 md:grow-0 md:basis-auto">
								<div class="grow lg:grow-0">
									<Switch>
										<Match when={facility().isBookable}>
											<Button
												as={Link}
												href={`/course/${facility().slug!}/reserve-tee-time`}
												class="shrink text-nowrap"
											>
												Book a tee time
											</Button>
										</Match>
										<Match when={facility().supportsTroonAccessPrive}>
											<Button
												as={Link}
												href={`/membership/request-tee-time?facility=${facility().slug}`}
												class="shrink text-nowrap"
											>
												Request a tee time
											</Button>
										</Match>
									</Switch>
								</div>
								<div class="shrink">
									<Button
										appearance="secondary"
										onClick={() => {
											const data = new FormData();
											data.set('facilityId', facility().id ?? '');
											if (!facility().isFavorite) {
												data.set('isFavorite', 'true');
											}
											handleFavoriteAction(data);
										}}
										class="text-xl"
									>
										<Show when={facility().isFavorite} fallback={<IconStar />}>
											<IconStarFilled />
										</Show>
										<span class="sr-only">Favorite</span>
									</Button>
								</div>
							</div>
						</div>
					</header>

					<Grid>
						<GridTwoThirds class="flex flex-col gap-8">
							<section class="flex flex-col gap-4">
								<h2 class="text-xl font-semibold md:text-2xl">Overview</h2>
								<p class="whitespace-pre-line" innerHTML={facility().description ?? ''} />
							</section>

							<Show when={facility().gallery.length}>
								<section class="flex flex-col gap-4">
									<h2 class="text-xl font-semibold md:text-2xl">Course photos</h2>
									<Carousel auto="play" description={`Photos of ${facility().name}`} outerClass="rounded-lg">
										<For each={facility().gallery}>
											{(item) => (
												<CarouselItem class="mx-2 w-full overflow-hidden rounded-lg">
													<Picture
														src={item}
														width={600}
														height={300}
														sizes="(max-width: 768px) 95vw, 50vw"
														class="w-full"
														loading="lazy"
														alt={`Landscape putting greens and fairway photo of the ${facility().name} golf course`}
													/>
												</CarouselItem>
											)}
										</For>
									</Carousel>
								</section>
							</Show>
						</GridTwoThirds>

						<GridThird>
							<Section>
								<FacilityLocation
									courses={facility().courses.map((c) => c.name)}
									name={facility().name}
									slug={facility().slug}
									address={facility().address}
									phone={facility().phone}
									website={facility().website}
								/>
							</Section>
						</GridThird>
					</Grid>
				</Container>
			</Suspense>
		</>
	);
}

const handleFavorite = clientAction(
	'post',
	'/v0/facilities/toggle-favorite',
	{},
	{ revalidate: ['/v0/facilities/{idOrSlug}', '/v0/facilities', '/v0/facilities/favorites'] },
);

export const route = { info: { nav: { hero: true } } } satisfies RouteDefinition;
